import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { archiveApi, ArchiveStatsResponse, ArchivedConversation, BulkArchiveResponse } from '../services/archiveApi';
import { RootState } from './index';

// Types
interface ArchiveState {
  // Messaging Archives
  messagingArchives: ArchivedConversation[];
  messagingArchiveCount: number;
  messagingHasNext: boolean;
  messagingHasPrevious: boolean;
  
  // Chatbot Archives
  chatbotArchives: ArchivedConversation[];
  chatbotArchiveCount: number;
  chatbotHasNext: boolean;
  chatbotHasPrevious: boolean;
  
  // Statistics
  messagingStats: ArchiveStatsResponse['data'] | null;
  chatbotStats: ArchiveStatsResponse['data'] | null;
  
  // UI State
  loading: boolean;
  bulkLoading: boolean;
  error: string | null;
  lastOperation: {
    type: 'archive' | 'restore' | 'bulk_archive' | 'bulk_restore' | null;
    success: boolean;
    message: string;
    timestamp: number;
  } | null;
  
  // Filters
  filters: {
    search: string;
    archiveReason: string;
    dateFrom: string;
    dateTo: string;
    sortBy: string;
    order: string;
  };
  
  // Pagination
  currentPage: number;
  pageSize: number;
}

const initialState: ArchiveState = {
  messagingArchives: [],
  messagingArchiveCount: 0,
  messagingHasNext: false,
  messagingHasPrevious: false,
  
  chatbotArchives: [],
  chatbotArchiveCount: 0,
  chatbotHasNext: false,
  chatbotHasPrevious: false,
  
  messagingStats: null,
  chatbotStats: null,
  
  loading: false,
  bulkLoading: false,
  error: null,
  lastOperation: null,
  
  filters: {
    search: '',
    archiveReason: '',
    dateFrom: '',
    dateTo: '',
    sortBy: 'archived_at',
    order: 'desc'
  },
  
  currentPage: 1,
  pageSize: 20
};

// Async Thunks

// Archive a messaging conversation
export const archiveConversation = createAsyncThunk(
  'archive/archiveConversation',
  async ({ conversationId, reason = 'manual', notes = '' }: { 
    conversationId: string; 
    reason?: string; 
    notes?: string 
  }) => {
    const response = await archiveApi.archiveConversation(conversationId, reason, notes);
    return { conversationId, response };
  }
);

// Restore a messaging conversation
export const restoreConversation = createAsyncThunk(
  'archive/restoreConversation',
  async ({ conversationId, notes = '' }: { 
    conversationId: string; 
    notes?: string 
  }) => {
    const response = await archiveApi.restoreConversation(conversationId, notes);
    return { conversationId, response };
  }
);

// Bulk archive conversations
export const bulkArchiveConversations = createAsyncThunk(
  'archive/bulkArchive',
  async ({ conversationIds, reason = 'bulk_action', notes = '' }: {
    conversationIds: string[];
    reason?: string;
    notes?: string;
  }) => {
    const response = await archiveApi.bulkArchiveConversations(conversationIds, reason, notes);
    return response;
  }
);

// Bulk restore conversations
export const bulkRestoreConversations = createAsyncThunk(
  'archive/bulkRestore',
  async ({ conversationIds, notes = '' }: {
    conversationIds: string[];
    notes?: string;
  }) => {
    const response = await archiveApi.bulkRestoreConversations(conversationIds, notes);
    return response;
  }
);

// Fetch archived messaging conversations
export const fetchArchivedConversations = createAsyncThunk(
  'archive/fetchArchivedConversations',
  async (_, { getState }) => {
    const state = getState() as RootState;
    const { filters, currentPage, pageSize } = state.archive;
    
    const response = await archiveApi.getArchivedConversations({
      ...filters,
      limit: pageSize,
      offset: (currentPage - 1) * pageSize
    });
    return response;
  }
);

// Fetch archive statistics
export const fetchArchiveStats = createAsyncThunk(
  'archive/fetchStats',
  async (type: 'messaging' | 'chatbot' | 'both' = 'both') => {
    const results: {
      messaging?: ArchiveStatsResponse;
      chatbot?: ArchiveStatsResponse;
    } = {};
    
    if (type === 'messaging' || type === 'both') {
      results.messaging = await archiveApi.getArchiveStats();
    }
    
    if (type === 'chatbot' || type === 'both') {
      results.chatbot = await archiveApi.getChatbotArchiveStats();
    }
    
    return results;
  }
);

// Archive a chatbot session
export const archiveChatbotSession = createAsyncThunk(
  'archive/archiveChatbotSession',
  async ({ sessionId, reason = 'manual', notes = '' }: {
    sessionId: string;
    reason?: string;
    notes?: string;
  }) => {
    const response = await archiveApi.archiveChatbotSession(sessionId, reason, notes);
    return { sessionId, response };
  }
);

// Restore a chatbot session
export const restoreChatbotSession = createAsyncThunk(
  'archive/restoreChatbotSession',
  async ({ sessionId }: { sessionId: string }) => {
    const response = await archiveApi.restoreChatbotSession(sessionId);
    return { sessionId, response };
  }
);

// Fetch archived chatbot sessions
export const fetchArchivedChatbotSessions = createAsyncThunk(
  'archive/fetchArchivedChatbotSessions',
  async (_, { getState }) => {
    const state = getState() as RootState;
    const { filters, currentPage, pageSize } = state.archive;
    
    const response = await archiveApi.getArchivedChatbotSessions({
      search: filters.search,
      date_from: filters.dateFrom,
      date_to: filters.dateTo,
      sort_by: filters.sortBy,
      order: filters.order,
      limit: pageSize,
      offset: (currentPage - 1) * pageSize
    });
    return response;
  }
);

// Auto-restore on new message
export const autoRestoreOnNewMessage = createAsyncThunk(
  'archive/autoRestore',
  async ({ conversationId, messageId }: {
    conversationId: string;
    messageId?: string;
  }) => {
    const response = await archiveApi.autoRestoreConversation(conversationId, 'new_message', messageId);
    return { conversationId, response };
  }
);

// Slice
const archiveSlice = createSlice({
  name: 'archive',
  initialState,
  reducers: {
    // Set filters
    setFilters: (state, action: PayloadAction<Partial<ArchiveState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.currentPage = 1; // Reset to first page when filters change
    },
    
    // Clear filters
    clearFilters: (state) => {
      state.filters = initialState.filters;
      state.currentPage = 1;
    },
    
    // Set pagination
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.currentPage = 1;
    },
    
    // Clear error
    clearError: (state) => {
      state.error = null;
    },
    
    // Clear last operation
    clearLastOperation: (state) => {
      state.lastOperation = null;
    },
    
    // Update archive status locally (optimistic update)
    updateConversationArchiveStatus: (state, action: PayloadAction<{
      conversationId: string;
      isArchived: boolean;
      archiveInfo?: any;
    }>) => {
      const { conversationId, isArchived } = action.payload;
      
      if (isArchived) {
        // Remove from archives list if restoring
        state.messagingArchives = state.messagingArchives.filter(
          conv => conv.id !== conversationId
        );
        state.messagingArchiveCount = Math.max(0, state.messagingArchiveCount - 1);
      }
    },
    
    // Update multiple conversations (bulk operations)
    updateMultipleConversations: (state, action: PayloadAction<{
      conversationIds: string[];
      isArchived: boolean;
    }>) => {
      const { conversationIds, isArchived } = action.payload;
      
      if (!isArchived) {
        // Remove restored conversations from archive list
        state.messagingArchives = state.messagingArchives.filter(
          conv => !conversationIds.includes(conv.id)
        );
        state.messagingArchiveCount = Math.max(
          0, 
          state.messagingArchiveCount - conversationIds.length
        );
      }
    }
  },
  extraReducers: (builder) => {
    // Archive conversation
    builder
      .addCase(archiveConversation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(archiveConversation.fulfilled, (state, action) => {
        state.loading = false;
        state.lastOperation = {
          type: 'archive',
          success: true,
          message: action.payload.response.message,
          timestamp: Date.now()
        };
      })
      .addCase(archiveConversation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to archive conversation';
        state.lastOperation = {
          type: 'archive',
          success: false,
          message: action.error.message || 'Failed to archive conversation',
          timestamp: Date.now()
        };
      });
    
    // Restore conversation
    builder
      .addCase(restoreConversation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(restoreConversation.fulfilled, (state, action) => {
        state.loading = false;
        // Remove from archived list
        state.messagingArchives = state.messagingArchives.filter(
          conv => conv.id !== action.payload.conversationId
        );
        state.messagingArchiveCount = Math.max(0, state.messagingArchiveCount - 1);
        state.lastOperation = {
          type: 'restore',
          success: true,
          message: action.payload.response.message,
          timestamp: Date.now()
        };
      })
      .addCase(restoreConversation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to restore conversation';
        state.lastOperation = {
          type: 'restore',
          success: false,
          message: action.error.message || 'Failed to restore conversation',
          timestamp: Date.now()
        };
      });
    
    // Bulk archive
    builder
      .addCase(bulkArchiveConversations.pending, (state) => {
        state.bulkLoading = true;
        state.error = null;
      })
      .addCase(bulkArchiveConversations.fulfilled, (state, action) => {
        state.bulkLoading = false;
        state.lastOperation = {
          type: 'bulk_archive',
          success: true,
          message: action.payload.message,
          timestamp: Date.now()
        };
      })
      .addCase(bulkArchiveConversations.rejected, (state, action) => {
        state.bulkLoading = false;
        state.error = action.error.message || 'Bulk archive failed';
        state.lastOperation = {
          type: 'bulk_archive',
          success: false,
          message: action.error.message || 'Bulk archive failed',
          timestamp: Date.now()
        };
      });
    
    // Bulk restore
    builder
      .addCase(bulkRestoreConversations.pending, (state) => {
        state.bulkLoading = true;
        state.error = null;
      })
      .addCase(bulkRestoreConversations.fulfilled, (state, action) => {
        state.bulkLoading = false;
        // Remove restored conversations from archive list
        if (action.payload.data?.restored_conversations) {
          state.messagingArchives = state.messagingArchives.filter(
            conv => !action.payload.data?.restored_conversations?.includes(conv.id)
          );
          state.messagingArchiveCount = Math.max(
            0,
            state.messagingArchiveCount - (action.payload.data?.successfully_restored || 0)
          );
        }
        state.lastOperation = {
          type: 'bulk_restore',
          success: true,
          message: action.payload.message,
          timestamp: Date.now()
        };
      })
      .addCase(bulkRestoreConversations.rejected, (state, action) => {
        state.bulkLoading = false;
        state.error = action.error.message || 'Bulk restore failed';
        state.lastOperation = {
          type: 'bulk_restore',
          success: false,
          message: action.error.message || 'Bulk restore failed',
          timestamp: Date.now()
        };
      });
    
    // Fetch archived conversations
    builder
      .addCase(fetchArchivedConversations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchArchivedConversations.fulfilled, (state, action) => {
        state.loading = false;
        state.messagingArchives = action.payload.results;
        state.messagingArchiveCount = action.payload.count;
        state.messagingHasNext = !!action.payload.next;
        state.messagingHasPrevious = !!action.payload.previous;
      })
      .addCase(fetchArchivedConversations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch archived conversations';
      });
    
    // Fetch stats
    builder
      .addCase(fetchArchiveStats.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchArchiveStats.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.messaging) {
          state.messagingStats = action.payload.messaging.data;
        }
        if (action.payload.chatbot) {
          state.chatbotStats = action.payload.chatbot.data;
        }
      })
      .addCase(fetchArchiveStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch statistics';
      });
    
    // Archive chatbot session
    builder
      .addCase(archiveChatbotSession.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(archiveChatbotSession.fulfilled, (state, action) => {
        state.loading = false;
        state.lastOperation = {
          type: 'archive',
          success: true,
          message: 'Chatbot session archived successfully',
          timestamp: Date.now()
        };
      })
      .addCase(archiveChatbotSession.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to archive chatbot session';
      });
    
    // Restore chatbot session
    builder
      .addCase(restoreChatbotSession.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(restoreChatbotSession.fulfilled, (state, action) => {
        state.loading = false;
        // Remove from archived list
        state.chatbotArchives = state.chatbotArchives.filter(
          session => session.id !== action.payload.sessionId
        );
        state.chatbotArchiveCount = Math.max(0, state.chatbotArchiveCount - 1);
        state.lastOperation = {
          type: 'restore',
          success: true,
          message: 'Chatbot session restored successfully',
          timestamp: Date.now()
        };
      })
      .addCase(restoreChatbotSession.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to restore chatbot session';
      });
    
    // Fetch archived chatbot sessions
    builder
      .addCase(fetchArchivedChatbotSessions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchArchivedChatbotSessions.fulfilled, (state, action) => {
        state.loading = false;
        state.chatbotArchives = action.payload.results;
        state.chatbotArchiveCount = action.payload.count;
        state.chatbotHasNext = !!action.payload.next;
        state.chatbotHasPrevious = !!action.payload.previous;
      })
      .addCase(fetchArchivedChatbotSessions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch archived chatbot sessions';
      });
    
    // Auto-restore
    builder
      .addCase(autoRestoreOnNewMessage.fulfilled, (state, action) => {
        // Remove from archived list if successful
        if (action.payload.response.success) {
          state.messagingArchives = state.messagingArchives.filter(
            conv => conv.id !== action.payload.conversationId
          );
          state.messagingArchiveCount = Math.max(0, state.messagingArchiveCount - 1);
        }
      });
  }
});

// Actions
export const {
  setFilters,
  clearFilters,
  setCurrentPage,
  setPageSize,
  clearError,
  clearLastOperation,
  updateConversationArchiveStatus,
  updateMultipleConversations
} = archiveSlice.actions;

// Selectors
export const selectArchiveState = (state: RootState) => state.archive;
export const selectMessagingArchives = (state: RootState) => state.archive.messagingArchives;
export const selectChatbotArchives = (state: RootState) => state.archive.chatbotArchives;
export const selectArchiveLoading = (state: RootState) => state.archive.loading;
export const selectArchiveBulkLoading = (state: RootState) => state.archive.bulkLoading;
export const selectArchiveError = (state: RootState) => state.archive.error;
export const selectArchiveFilters = (state: RootState) => state.archive.filters;
export const selectMessagingStats = (state: RootState) => state.archive.messagingStats;
export const selectChatbotStats = (state: RootState) => state.archive.chatbotStats;
export const selectLastOperation = (state: RootState) => state.archive.lastOperation;

export default archiveSlice.reducer;